import React, { useState, useEffect, useMemo } from 'react';
import './MyTeamsPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { gameDayToShortDate } from '../utils/dateUtils';
import { FUNCTIONS_URL } from '../services/supabaseConfig';
import BoxScore from '../components/BoxScore';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import { numberToGrade, getAttrColor } from '../utils/grades';

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  division: string;
  conference: string;
  wins: number;
  losses: number;
  runs_scored?: number;
  runs_allowed?: number;
  is_ai?: boolean;
  owner_id?: string;
  league_id?: string;
  primary_color?: string;
  secondary_color?: string;
  uniform_style?: string;
  strategy?: any;
  batting_order?: string[];
  rotation_order?: string[];
  depth_chart?: Record<string, string[]>;
}

interface PlayerFromDB {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  power: number;
  contact: number;
  speed: number;
  discipline: number;
  fielding: number;
  arm: number;
  endurance: number;
  velocity: number;
  control: number;
  movement: number;
  current_stamina: number;
}

interface ScheduleGame {
  id: string;
  game_id?: string;
  game_day: number;
  home_team_id: string;
  away_team_id: string;
  home_score: number | null;
  away_score: number | null;
  status: string;
  home_team?: { city: string; name: string };
  away_team?: { city: string; name: string };
  // Pitcher decisions (loaded separately)
  win_pitcher?: { id: string; name: string };
  loss_pitcher?: { id: string; name: string };
  save_pitcher?: { id: string; name: string };
}

interface StrategySettings {
  // Offense
  hittingApproach: number;
  stealAttempts: number;
  baseRunningAggression: number;
  sacrificeBunts: number;
  intentionalWalks: number;
  // Pitching / Defense
  pitcherManagement: number;
  shiftFrequency: number;
  bullpenUsage: number;
  // Lineup
  defenseFirst: number;
  lineupProtection: number;
}

interface UniformSettings {
  primaryColor: string;
  secondaryColor: string;
  style: 'pinstripe' | 'solid' | 'gradient' | 'v-neck' | 'classic';
  capColor: string;
  jerseyNumber: string;
}

const DEFAULT_STRATEGY: StrategySettings = {
  hittingApproach: 50,
  stealAttempts: 50,
  baseRunningAggression: 50,
  sacrificeBunts: 50,
  intentionalWalks: 50,
  pitcherManagement: 50,
  shiftFrequency: 50,
  bullpenUsage: 50,
  defenseFirst: 50,
  lineupProtection: 50,
};

const DEFAULT_UNIFORM: UniformSettings = {
  primaryColor: '#1a3a5c',
  secondaryColor: '#c4a35a',
  style: 'pinstripe',
  capColor: '#1a3a5c',
  jerseyNumber: '00',
};

const UNIFORM_STYLES = [
  { value: 'pinstripe', label: 'Pinstripe', desc: 'Classic thin stripes' },
  { value: 'solid', label: 'Solid', desc: 'Clean solid color' },
  { value: 'gradient', label: 'Gradient', desc: 'Fading two-tone' },
  { value: 'v-neck', label: 'V-Neck', desc: 'Contrast V-neck collar' },
  { value: 'classic', label: 'Classic', desc: 'Traditional button-down' },
];

const TEAM_COLORS = [
  '#1a3a5c', '#c41230', '#0c2340', '#003831', '#ce1141',
  '#e81828', '#002d72', '#27251f', '#e31937', '#004687',
  '#002b5c', '#bd3039', '#0c2340', '#333366', '#c41e3a',
  '#fb4f14', '#003263', '#ff5910', '#002d62', '#ab0003',
  '#572d2d', '#2f2f2f', '#4a2c7a', '#006bb6', '#134a8e',
];

const TIER_NAMES = ['', 'Elite', 'Platinum', 'Gold', 'Silver', 'Bronze'];
const TIER_COLORS = ['', '#b9f2ff', '#e5e4e2', '#ffd700', '#c0c0c0', '#cd7f32'];
const TEAM_COST = 10000;

const POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'];
const BATTING_SLOTS = 9;

interface MyTeamsPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

type TeamTab = 'roster' | 'lineup' | 'playerStats' | 'teamStats' | 'schedule' | 'boosts' | 'offers' | 'strategy' | 'uniforms';

const MyTeamsPage: React.FC<MyTeamsPageProps> = ({ onNavigate, userData }) => {
  const [ownedTeams, setOwnedTeams] = useState<TeamFromDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<TeamFromDB | null>(null);
  const [activeTab, setActiveTab] = useState<TeamTab>(() => (window.location.hash?.slice(1) as TeamTab) || 'roster');

  // Sync tab to URL hash so back button works
  useEffect(() => {
    if (window.location.hash?.slice(1) !== activeTab) {
      window.history.replaceState({}, '', `#${activeTab}`);
    }
  }, [activeTab]);

  // Team data
  const [roster, setRoster] = useState<PlayerFromDB[]>([]);
  const [myFreeAgents, setMyFreeAgents] = useState<PlayerFromDB[]>([]);
  const [schedule, setSchedule] = useState<ScheduleGame[]>([]);
  const [strategy, setStrategy] = useState<StrategySettings>(DEFAULT_STRATEGY);
  const [lineup, setLineup] = useState<string[]>([]);
  const [pitchingRotation, setPitchingRotation] = useState<string[]>([]);
  const [depthChart, setDepthChart] = useState<Record<string, (string | null)[]>>({});

  // Player stats (loaded from DB)
  const [playerStats, setPlayerStats] = useState<Map<string, any>>(new Map());

  // Boxscore modal
  const [boxscoreGameId, setBoxscoreGameId] = useState<string | null>(null);

  // Roster sort state
  const [hitterSort, setHitterSort] = useState<{key: string; dir: 'asc'|'desc'}>({key: 'overall', dir: 'desc'});
  const [pitcherSort, setPitcherSort] = useState<{key: string; dir: 'asc'|'desc'}>({key: 'overall', dir: 'desc'});
  
  // Standings data (wins/losses from standings table, not teams table)
  const [teamStanding, setTeamStanding] = useState<{wins: number; losses: number; runs_scored: number; runs_allowed: number} | null>(null);

  // Buy team modal
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [newTeamCity, setNewTeamCity] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamAbbr, setNewTeamAbbr] = useState('');
  const [userBalance, setUserBalance] = useState<number>(0);
  const [buying, setBuying] = useState(false);
  const [queuedTeams, setQueuedTeams] = useState<any[]>([]);

  // Uniform editor
  const [uniform, setUniform] = useState<UniformSettings>(DEFAULT_UNIFORM);

  // Contract offers
  const [receivedOffers, setReceivedOffers] = useState<any[]>([]);
  const [sentOffers, setSentOffers] = useState<any[]>([]);
  const [faOfferCounts, setFaOfferCounts] = useState<Record<string, number>>({});
  const [acceptingOfferId, setAcceptingOfferId] = useState<string | null>(null);
  const [withdrawingOfferId, setWithdrawingOfferId] = useState<string | null>(null);
  const [sentCompetingOffers, setSentCompetingOffers] = useState<Record<string, any[]>>({});

  // Boosts
  const [myBoosts, setMyBoosts] = useState<any[]>([]);
  const [forgeSelection, setForgeSelection] = useState<string[]>([]);
  const [forgeResult, setForgeResult] = useState<any>(null);
  const [boostLoading, setBoostLoading] = useState(false);
  const [equipModalBoost, setEquipModalBoost] = useState<any>(null);
  const [equipModalPlayer, setEquipModalPlayer] = useState<string | null>(null);

  useEffect(() => {
    loadTeams();
  }, [userData?.id]);

  // Handle ?team= query param — auto-select team from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamIdParam = params.get('team');
    if (teamIdParam && ownedTeams.length > 0) {
      const match = ownedTeams.find(t => t.id === teamIdParam);
      if (match) {
        setSelectedTeam(match);
        // Clean URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete('team');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [ownedTeams]);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamData();
      if (selectedTeam.primary_color) {
        setUniform({
          primaryColor: selectedTeam.primary_color,
          secondaryColor: selectedTeam.secondary_color || '#ffffff',
          style: (selectedTeam.uniform_style as any) || 'solid',
          capColor: selectedTeam.primary_color,
          jerseyNumber: '00',
        });
      }
    }
  }, [selectedTeam]);

  // Load contract offers for user's free agents
  async function loadReceivedOffers() {
    if (!userData?.id || !isSupabaseConfigured() || !supabase) return;

    // Get user's FA player IDs (all leagues)
    const { data: myFAPlayers } = await supabase
      .from('players')
      .select('id, first_name, last_name, position, overall')
      .eq('user_id', userData.id)
      .is('team_id', null);

    if (!myFAPlayers || myFAPlayers.length === 0) {
      setReceivedOffers([]);
      setFaOfferCounts({});
      return;
    }

    const faIds = myFAPlayers.map(p => p.id);

    // Get all offers for these players
    const { data: offers } = await supabase
      .from('contract_offers')
      .select('id, player_id, team_id, team_name, team_city, salary, duration, bonuses, status, expires_at, created_at')
      .in('player_id', faIds)
      .order('created_at', { ascending: false });

    // Build offer counts per player
    const counts: Record<string, number> = {};
    (offers || []).forEach(o => {
      if (o.status === 'pending') {
        counts[o.player_id] = (counts[o.player_id] || 0) + 1;
      }
    });

    // Enrich offers with player info
    const playerMap = new Map(myFAPlayers.map(p => [p.id, p]));
    const enriched = (offers || []).map(o => ({
      ...o,
      player_first_name: playerMap.get(o.player_id)?.first_name || '',
      player_last_name: playerMap.get(o.player_id)?.last_name || '',
      player_position: playerMap.get(o.player_id)?.position || '',
      player_overall: playerMap.get(o.player_id)?.overall || 0,
    }));

    setReceivedOffers(enriched);
    setFaOfferCounts(counts);
  }

  // Load contract offers sent BY this user's teams
  async function loadSentOffers() {
    if (!userData?.id || !isSupabaseConfigured() || !supabase) return;

    // Get all team IDs owned by this user
    const { data: myTeams } = await supabase
      .from('teams')
      .select('id')
      .eq('owner_id', userData.id);

    if (!myTeams || myTeams.length === 0) {
      setSentOffers([]);
      return;
    }

    const teamIds = myTeams.map(t => t.id);

    // Get all offers sent by these teams
    const { data: offers } = await supabase
      .from('contract_offers')
      .select('id, player_id, team_id, team_name, team_city, salary, duration, bonuses, status, expires_at, created_at, player_position, tier')
      .in('team_id', teamIds)
      .order('created_at', { ascending: false });

    // Get player info for each offer
    const playerIds = Array.from(new Set((offers || []).map(o => o.player_id)));
    let playerMap = new Map<string, any>();
    if (playerIds.length > 0) {
      const { data: players } = await supabase
        .from('players')
        .select('id, first_name, last_name, position, overall, team_id, avatar_url')
        .in('id', playerIds);
      (players || []).forEach(p => playerMap.set(p.id, p));
    }

    const enriched = (offers || []).map(o => ({
      ...o,
      player_first_name: playerMap.get(o.player_id)?.first_name || 'Unknown',
      player_last_name: playerMap.get(o.player_id)?.last_name || '',
      player_position: playerMap.get(o.player_id)?.position || o.player_position || '',
      player_overall: playerMap.get(o.player_id)?.overall || 0,
      player_signed: !!playerMap.get(o.player_id)?.team_id,
      player_avatar: playerMap.get(o.player_id)?.avatar_url || null,
    }));

    setSentOffers(enriched);

    // Load competing offers for all pending sent offers
    const pendingSent = enriched.filter((o: any) => o.status === 'pending');
    if (pendingSent.length > 0 && supabase) {
      loadCompetingForSentOffers(pendingSent);
    }
  }

  // Load competing offers for pending sent offers (so owners see what others are bidding)
  async function loadCompetingForSentOffers(pendingSent: any[]) {
    if (!supabase) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const myTeamIds = new Set((await supabase.from('teams').select('id').eq('owner_id', userData?.id)).data?.map((t: any) => t.id) || []);

    const newCompeting: Record<string, any[]> = {};
    // Batch: for each pending sent offer, fetch competing offers
    await Promise.all(pendingSent.map(async (o: any) => {
      try {
        const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'player-offers', playerId: o.player_id }),
        });
        const data = await res.json();
        if (data.offers) {
          // Filter out our own team's offers
          newCompeting[o.player_id] = data.offers.filter((co: any) => !myTeamIds.has(co.team_id));
        }
      } catch {}
    }));

    setSentCompetingOffers(prev => ({ ...prev, ...newCompeting }));
  }

  async function handleWithdrawOffer(offerId: string) {
    if (!isSupabaseConfigured() || !supabase) return;
    setWithdrawingOfferId(offerId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert('Not signed in'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'withdraw-offer', offerId }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to withdraw offer');
        return;
      }

      alert('Offer withdrawn successfully');
      loadSentOffers();
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setWithdrawingOfferId(null);
    }
  }

  // ============================================================
  // Boost functions
  // ============================================================
  async function loadMyBoosts() {
    if (!isSupabaseConfigured() || !supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'list-boosts' }),
      });
      const data = await res.json();
      if (res.ok) {
        setMyBoosts(data.boosts || []);
      }
    } catch (err) {
      console.error('Load boosts error:', err);
    }
  }

  async function handleEquipBoost(boostId: string, playerId: string) {
    if (!isSupabaseConfigured() || !supabase) return;
    setBoostLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert('Not signed in'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'equip-boost', boostId, playerId }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to equip boost');
        return;
      }

      alert(`✅ ${data.message}`);
      loadMyBoosts();
      loadTeamData();
      setEquipModalBoost(null);
      setEquipModalPlayer(null);
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setBoostLoading(false);
    }
  }

  async function handleUnequipBoost(boostId: string) {
    if (!isSupabaseConfigured() || !supabase) return;
    setBoostLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert('Not signed in'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'unequip-boost', boostId }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to unequip boost');
        return;
      }

      alert('Boost unequipped');
      loadMyBoosts();
      loadTeamData();
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setBoostLoading(false);
    }
  }

  async function handleForgeCombine() {
    if (forgeSelection.length !== 2) return;
    if (!isSupabaseConfigured() || !supabase) return;
    setBoostLoading(true);
    setForgeResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert('Not signed in'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/boost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'forge-combine', boostId1: forgeSelection[0], boostId2: forgeSelection[1] }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Forge combine failed');
        return;
      }

      setForgeResult(data.result);
      setForgeSelection([]);
      loadMyBoosts();
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setBoostLoading(false);
    }
  }

  // Load boosts when user logs in or switches to boosts tab
  useEffect(() => {
    if (userData?.id && isSupabaseConfigured() && supabase && activeTab === 'boosts') {
      loadMyBoosts();
    }
  }, [userData?.id, activeTab]);

  // Also load boosts on mount so inventory is visible even without a team selected
  useEffect(() => {
    if (userData?.id && isSupabaseConfigured() && supabase) {
      loadMyBoosts();
    }
  }, [userData?.id]);

  async function handleAcceptOffer(offerId: string) {
    if (!isSupabaseConfigured() || !supabase) return;
    setAcceptingOfferId(offerId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { alert('Not signed in'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'accept-offer', offerId }),
      });

      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to accept offer');
        return;
      }

      alert(`✅ ${data.playerName} signed with ${data.teamName}!`);
      loadReceivedOffers();
      loadSentOffers();
      loadTeamData();
    } catch (err: any) {
      alert('Error: ' + err.message);
    } finally {
      setAcceptingOfferId(null);
    }
  }

  async function handleDeclineOffer(offerId: string) {
    if (!isSupabaseConfigured() || !supabase) return;

    try {
      const { error } = await supabase
        .from('contract_offers')
        .update({ status: 'rejected' })
        .eq('id', offerId);

      if (error) {
        alert('Error declining offer: ' + error.message);
        return;
      }

      loadReceivedOffers();
      loadSentOffers();
    } catch (err: any) {
      alert('Error declining offer: ' + err.message);
    }
  }

  useEffect(() => {
    if (userData?.id && isSupabaseConfigured() && supabase) {
      loadReceivedOffers();
      loadSentOffers();
    }
  }, [userData?.id, myFreeAgents]);

  async function loadTeams() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }

    const userId = userData?.id;
    if (userId) {
      // Load user balance
      const { data: userRow } = await supabase
        .from('users')
        .select('balls_balance')
        .eq('id', userId)
        .single();
      setUserBalance(userRow?.balls_balance || 0);

      // Load only teams owned by this user in traditional (regular) leagues
      // Get ONLY traditional league IDs
      const { data: tradLeagues } = await supabase
        .from('leagues')
        .select('id')
        .in('league_type', TRAD_ARRAY as readonly string[]);
      const tradLeagueIds = (tradLeagues || []).map(l => l.id);

      let ownedQuery = supabase
        .from('teams')
        .select('*')
        .eq('owner_id', userId);
      if (tradLeagueIds.length > 0) {
        ownedQuery = ownedQuery.in('league_id', tradLeagueIds);
      }

      const { data: owned } = await ownedQuery;
      setOwnedTeams(owned || []);

      // Load user's queued teams (pending assignment)
      // Filter out any queue entries whose name matches an already-owned team
      const { data: queueData } = await supabase
        .from('team_queue')
        .select('*')
        .eq('owner_id', userId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      const ownedNames = new Set((owned || []).map(t => t.name.toLowerCase()));
      const activeQueue = (queueData || []).filter(qt => !ownedNames.has(qt.name.toLowerCase()));
      setQueuedTeams(activeQueue);
    } else {
      setOwnedTeams([]);
    }
    setLoading(false);
  }

  async function loadTeamData() {
    if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;

    const { data: players } = await supabase
      .from('players')
      .select('*')
      .eq('team_id', selectedTeam.id)
      .order('overall', { ascending: false });
    setRoster(players || []);

    // Load standings for wins/losses display
    if (selectedTeam.league_id) {
      const { data: leagueInfo } = await supabase
        .from('leagues')
        .select('season_year')
        .eq('id', selectedTeam.league_id)
        .single();
      const seasonYear = leagueInfo?.season_year || 1913;
      const { data: standings } = await supabase
        .from('league_standings')
        .select('wins, losses, runs_scored, runs_allowed')
        .eq('team_id', selectedTeam.id)
        .eq('season_year', seasonYear)
        .single();
      if (standings) setTeamStanding(standings);
    }

    // Load player season stats for the current season
    if (players && players.length > 0 && selectedTeam.league_id) {
      const { data: leagueData } = await supabase.from('leagues').select('season_year').eq('id', selectedTeam.league_id).single();
      const seasonYear = leagueData?.season_year || new Date().getFullYear();
      const playerIds = players.map(p => p.id);
      const statsMap = new Map<string, any>();
      for (let i = 0; i < playerIds.length; i += 100) {
        const batch = playerIds.slice(i, i + 100);
        const { data: statsData } = await supabase.from('player_season_stats').select('*').in('player_id', batch).eq('season_year', seasonYear);
        if (statsData) statsData.forEach(s => statsMap.set(s.player_id, s));
      }
      setPlayerStats(statsMap);
    }

    // Load user's own created free agents in this league
    if (userData?.id && selectedTeam.league_id) {
      const { data: faPlayers } = await supabase
        .from('players')
        .select('*')
        .eq('user_id', userData.id)
        .is('team_id', null)
        .eq('league_id', selectedTeam.league_id)
        .order('overall', { ascending: false });
      setMyFreeAgents(faPlayers || []);
    } else {
      setMyFreeAgents([]);
    }

    if (players && lineup.length === 0) {
      const hitters = players.filter(p => p.is_hitter).sort((a, b) => b.overall - a.overall);
      const sp = players.filter(p => !p.is_hitter).sort((a, b) => b.overall - a.overall);
      setLineup(hitters.slice(0, BATTING_SLOTS).map(p => p.id));
      setPitchingRotation(sp.map(p => p.id));
    }

    const { data: games } = await supabase
      .from('league_schedule')
      .select('*')
      .or(`home_team_id.eq.${selectedTeam.id},away_team_id.eq.${selectedTeam.id}`)
      .order('game_day', { ascending: true })
      .limit(50);

    if (games && games.length > 0) {
      // Load team names for opponent display
      const oppTeamIds = new Set(games.map((g: any) => g.home_team_id === selectedTeam.id ? g.away_team_id : g.home_team_id));
      const { data: oppTeams } = await supabase
        .from('teams')
        .select('id, city, name')
        .in('id', Array.from(oppTeamIds));
      const oppMap = new Map<string, any>();
      (oppTeams || []).forEach((t: any) => oppMap.set(t.id, t));

      // Load pitcher decisions for completed games
      const completedIds = games.filter((g: any) => g.status === 'completed').map((g: any) => g.id);
      let pitcherMap = new Map<string, any>();
      if (completedIds.length > 0) {
        const { data: pitcherStats } = await supabase
          .from('player_game_stats')
          .select('game_id, player_id, win, loss, save')
          .in('game_id', completedIds)
          .or('win.eq.true,loss.eq.true,save.eq.true');

        if (pitcherStats) {
          // Get player names
          const pIds = Array.from(new Set(pitcherStats.map((s: any) => s.player_id)));
          const { data: pitcherNames } = await supabase
            .from('players')
            .select('id, first_name, last_name')
            .in('id', pIds);
          const nameMap = new Map<string, string>();
          (pitcherNames || []).forEach((p: any) => nameMap.set(p.id, `${p.first_name} ${p.last_name}`));

          pitcherStats.forEach((s: any) => {
            const key = `${s.game_id}-${s.player_id}`;
            pitcherMap.set(key, { ...s, name: nameMap.get(s.player_id) || 'Unknown' });
          });
        }
      }

      // Attach pitcher data to games
      const enriched = games.map((g: any) => {
        const entries = Array.from(pitcherMap.values()).filter((p: any) => p.game_id === g.id);
        const isHome = g.home_team_id === selectedTeam.id;
        const oppId = isHome ? g.away_team_id : g.home_team_id;
        const oppTeam = oppMap.get(oppId);
        return {
          ...g,
          opp_team: oppTeam || { city: '', name: 'Unknown' },
          opp_id: oppId,
          win_pitcher: entries.find((p: any) => p.win),
          loss_pitcher: entries.find((p: any) => p.loss),
          save_pitcher: entries.find((p: any) => p.save),
        };
      });
      setSchedule(enriched);
    }

    // Load team strategy
    if (selectedTeam.strategy) {
      setStrategy({ ...DEFAULT_STRATEGY, ...selectedTeam.strategy });
    } else {
      setStrategy(DEFAULT_STRATEGY);
    }

    // Load saved lineup, rotation, and depth chart
    if (selectedTeam.batting_order?.length) {
      setLineup(selectedTeam.batting_order);
    }
    if (selectedTeam.rotation_order?.length) {
      setPitchingRotation(selectedTeam.rotation_order);
    }
    if (selectedTeam.depth_chart && typeof selectedTeam.depth_chart === 'object') {
      // Load saved depth chart, padding each position to 3 slots
      const dc: Record<string, (string | null)[]> = {};
      POSITIONS.forEach(pos => {
        const saved = (selectedTeam.depth_chart as Record<string, string[]>)[pos] || [];
        dc[pos] = [saved[0] || null, saved[1] || null, saved[2] || null];
      });
      // Also add pitcher slots (P1-P11 for 11 pitching slots)
      // Fall back to rotation_order for any empty P slot
      const ro = selectedTeam.rotation_order;
      for (let i = 1; i <= 11; i++) {
        const key = `P${i}`;
        const saved = (selectedTeam.depth_chart as Record<string, string[]>)[key] || [];
        const fallback = ro?.[i-1] || null;
        dc[key] = [saved[0] || fallback || null, null, null];
      }
      setDepthChart(dc);
    } else {
      // Auto-generate depth chart from roster
      const dc: Record<string, (string | null)[]> = {};
      POSITIONS.forEach(pos => { dc[pos] = [null, null, null]; });
      // Pitcher slots ranked by overall
      for (let i = 1; i <= 11; i++) { dc[`P${i}`] = [null, null, null]; }
      if (players) {
        const hitters = players.filter(p => p.is_hitter);
        const pitchers = players.filter(p => !p.is_hitter);
        // Assign hitters by primary position, sorted by overall
        POSITIONS.forEach(pos => {
          const candidates = hitters.filter(p => p.position === pos).sort((a, b) => b.overall - a.overall);
          dc[pos] = [candidates[0]?.id || null, candidates[1]?.id || null, candidates[2]?.id || null];
        });
        // Assign pitchers ranked by overall
        const sortedPitchers = pitchers.sort((a, b) => b.overall - a.overall);
        sortedPitchers.forEach((p, idx) => {
          if (idx < 11) {
            dc[`P${idx + 1}`] = [p.id, null, null];
          }
        });
      }
      setDepthChart(dc);
    }
  }

  async function handleBuyTeam() {
    if (!userData?.id || !isSupabaseConfigured() || !supabase) {
      console.error('handleBuyTeam: missing userData or supabase', { userData, isSupabaseConfigured: isSupabaseConfigured() });
      alert('Not signed in. Please sign in first.');
      return;
    }
    if (buying) return;
    setBuying(true);

    // Validate inputs
    if (!newTeamName.trim() || newTeamAbbr.length < 2) {
      alert('Enter a team name and 2-3 letter abbreviation.');
      setBuying(false);
      return;
    }

    try {
      // INSERT TO QUEUE FIRST — only deduct $BALLS if queue succeeds
      const queueId = crypto.randomUUID();
      const { error: queueError } = await supabase
        .from('team_queue')
        .insert({
          id: queueId,
          city: newTeamCity.trim(),
          name: newTeamName.trim(),
          abbreviation: newTeamAbbr.trim().toUpperCase(),
          owner_id: userData.id,
          requested_tier: 5,
          status: 'pending',
        });

      if (queueError) {
        console.error('Queue insert error:', queueError);
        alert('Error queuing team: ' + queueError.message);
        setBuying(false);
        return;
      }

      // Queue succeeded — now deduct $BALLS
      const { deductBalls: deduct } = await import('../services/BallsService');
      const result = await deduct(userData.id, TEAM_COST, 'team_purchase', `Team purchase: ${newTeamCity} ${newTeamName}`, queueId, 'team_queue');

      if (!result.success) {
        // Deduction failed — remove queue entry so we don't leak it
        await supabase.from('team_queue').delete().eq('id', queueId);
        alert(`Purchase failed: ${result.error || 'Insufficient balance'}. You have ${(result.balance || 0).toLocaleString()} $BALLS, need ${TEAM_COST.toLocaleString()}.`);
        setBuying(false);
        return;
      }

      setUserBalance(result.balance || 0);
      alert(`✅ ${newTeamCity ? newTeamCity + ' ' : ''}${newTeamName} (${newTeamAbbr}) purchased for ${TEAM_COST.toLocaleString()} $BALLS!\n\nYour team will be added to a league when the new season begins.`);
      setShowBuyModal(false);
      document.body.style.overflow = '';
      setNewTeamCity('');
      setNewTeamName('');
      setNewTeamAbbr('');
      loadTeams();
    } catch (err) {
      console.error('handleBuyTeam error:', err);
      alert('Unexpected error: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setBuying(false);
    }
  }

  async function handleSaveUniform() {
    if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;

    const { error } = await supabase
      .from('teams')
      .update({
        primary_color: uniform.primaryColor,
        secondary_color: uniform.secondaryColor,
        uniform_style: uniform.style,
      })
      .eq('id', selectedTeam.id);

    if (error) {
      alert('Error saving uniform: ' + error.message);
      return;
    }

    alert('Uniform saved!');
    setSelectedTeam(prev => prev ? {
      ...prev,
      primary_color: uniform.primaryColor,
      secondary_color: uniform.secondaryColor,
      uniform_style: uniform.style,
    } : null);
  }

  async function handleSaveStrategy() {
    if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;

    // Owner lock: verify the logged-in user actually owns this team
    const userId = userData?.id;
    if (!userId || selectedTeam.owner_id !== userId) {
      alert('You can only edit strategy for teams you own.');
      return;
    }

    const { error } = await supabase
      .from('teams')
      .update({ strategy })
      .eq('id', selectedTeam.id)
      .eq('owner_id', userId); // double-lock at DB level too

    if (error) {
      alert('Error saving strategy: ' + error.message);
      return;
    }

    setSelectedTeam(prev => prev ? { ...prev, strategy } : null);
    alert('Strategy saved!');
  }

  function getPlayer(id: string): PlayerFromDB | undefined {
    return roster.find(p => p.id === id);
  }



  function buildDepthChart(): Record<string, PlayerFromDB[]> {
    const chart: Record<string, PlayerFromDB[]> = {};
    POSITIONS.forEach(pos => { chart[pos] = []; });
    roster.filter(p => p.is_hitter).forEach(p => {
      if (chart[p.position]) chart[p.position].push(p);
    });
    Object.keys(chart).forEach(pos => {
      chart[pos].sort((a, b) => b.overall - a.overall);
    });
    return chart;
  }

  // ===== UNIFORM PREVIEW =====
  const renderUniformPreview = () => {
    const pinstripe = uniform.style === 'pinstripe';
    const gradient = uniform.style === 'gradient';
    const vNeck = uniform.style === 'v-neck';

    return (
      <div className="uniform-preview">
        <div className="uniform-jersey" style={{
          background: gradient
            ? `linear-gradient(180deg, ${uniform.primaryColor}, ${uniform.secondaryColor})`
            : uniform.primaryColor,
          borderColor: uniform.secondaryColor,
        }}>
          {pinstripe && (
            <div className="pinstripe-overlay" style={{
              backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 8px, ${uniform.secondaryColor}22 8px, ${uniform.secondaryColor}22 9px)`,
            }} />
          )}
          {vNeck && (
            <div className="vneck-overlay" style={{ borderColor: uniform.secondaryColor }} />
          )}
          <div className="jersey-number" style={{ color: uniform.secondaryColor }}>
            {uniform.jerseyNumber}
          </div>
        </div>
        <div className="uniform-cap" style={{ background: uniform.capColor }}>
          <span className="cap-abbr">{selectedTeam?.abbreviation || 'TM'}</span>
        </div>
      </div>
    );
  };

  // ===== RENDER TABS =====
  // Resolve depth-chart position for each player (roster display)
  const depthChartPos = useMemo(() => {
    const map: Record<string, string> = {};
    if (selectedTeam?.depth_chart && typeof selectedTeam.depth_chart === 'object') {
      const dc = selectedTeam.depth_chart as Record<string, string[]>;
      const hitterPositions = ['C','1B','2B','3B','SS','LF','CF','RF','DH'];
      // PASS 1: label starters (chain[0]) first — this ensures Nash Mercer gets "3B" not "C (B3)"
      for (const pos of hitterPositions) {
        const chain = dc[pos];
        if (chain && Array.isArray(chain) && chain[0]) {
          map[chain[0]] = pos;
        }
      }
      // PASS 2: label backups — only for players not already labeled as starters
      for (const pos of hitterPositions) {
        const chain = dc[pos];
        if (chain && Array.isArray(chain)) {
          chain.forEach((pid, idx) => {
            if (pid && !map[pid]) map[pid] = `${pos} (B${idx+1})`;
          });
        }
      }
      // Pitcher roles
      const ROLE_MAP: Record<number, string> = {1:'SP1',2:'SP2',3:'SP3',4:'SP4',5:'SP5',6:'MR',7:'RP1',8:'RP2',9:'SU1',10:'SU2',11:'CL'};
      for (let i = 1; i <= 11; i++) {
        const chain = dc[`P${i}`];
        if (chain && Array.isArray(chain) && chain[0] && !map[chain[0]]) {
          map[chain[0]] = ROLE_MAP[i];
        }
      }
    }
    return map;
  }, [selectedTeam?.depth_chart]);

  const renderRoster = () => {
    const hitters = roster.filter(p => p.is_hitter);
    const pitchers = roster.filter(p => !p.is_hitter);

    const POSITION_ORDER: Record<string, number> = { C: 1, '1B': 2, '2B': 3, SS: 4, '3B': 5, LF: 6, CF: 7, RF: 8, SP: 9, RP: 10, CL: 11 };

    const sortPlayers = (players: PlayerFromDB[], sort: {key: string; dir: 'asc'|'desc'}) => {
      const sorted = [...players];
      sorted.sort((a: any, b: any) => {
        let aVal = a[sort.key] ?? 0;
        let bVal = b[sort.key] ?? 0;
        if (sort.key === 'position') { aVal = POSITION_ORDER[aVal] ?? 99; bVal = POSITION_ORDER[bVal] ?? 99; }
        if (typeof aVal === 'string') return sort.dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        return sort.dir === 'asc' ? aVal - bVal : bVal - aVal;
      });
      return sorted;
    };

    const toggleSort = (current: {key: string; dir: 'asc'|'desc'}, key: string, setter: (s: {key: string; dir: 'asc'|'desc'}) => void) => {
      if (current.key === key) setter({key, dir: current.dir === 'desc' ? 'asc' : 'desc'});
      else setter({key, dir: 'desc'});
    };

    const sortArrow = (sort: {key: string; dir: 'asc'|'desc'}, key: string) => {
      if (sort.key !== key) return ' ⇅';
      return sort.dir === 'desc' ? ' ▼' : ' ▲';
    };

    const sortedHitters = sortPlayers(hitters, hitterSort);
    const sortedPitchers = sortPlayers(pitchers, pitcherSort);
    return (
      <div className="roster-tab">
        <div className="roster-actions-bar">
          <span className="roster-count-label">Roster: {roster.length}/25</span>
          {roster.length < 25 && (
            <div className="generate-player-row">
              <select className="gen-pos-select" id="gen-pos">
                {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                <option value="P">P</option>
              </select>
              <button className="gen-player-btn" onClick={async () => {
                const pos = (document.getElementById('gen-pos') as HTMLSelectElement)?.value || '1B';
                const { generateAIPlayer } = await import('../services/AIContractService');
                const result = await generateAIPlayer(selectedTeam!.id, pos);
                if (result.success) {
                  alert(result.message);
                  loadTeamData();
                } else {
                  alert(result.message);
                }
              }}>
                🤖 Generate AI Player
              </button>
            </div>
          )}
        </div>
        <div className="roster-section">
          <h3>Hitters ({hitters.length})</h3>
          <table className="roster-table">
            <thead><tr><th>Name</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'position', setHitterSort)}>Pos{sortArrow(hitterSort, 'position')}</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'overall', setHitterSort)}>OVR{sortArrow(hitterSort, 'overall')}</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'power', setHitterSort)}>PWR{sortArrow(hitterSort, 'power')}</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'contact', setHitterSort)}>CON{sortArrow(hitterSort, 'contact')}</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'speed', setHitterSort)}>SPD{sortArrow(hitterSort, 'speed')}</th><th className="sortable-th" onClick={() => toggleSort(hitterSort, 'fielding', setHitterSort)}>FLD{sortArrow(hitterSort, 'fielding')}</th><th>STA</th><th></th></tr></thead>
            <tbody>
              {sortedHitters.map(p => (
                <tr key={p.id} className="roster-row">
                  <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}>{depthChartPos[p.id] || p.position}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)} style={{ color: getAttrColor(p.overall) }}>{numberToGrade(p.overall)}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.power) }}>{numberToGrade(p.power)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.contact) }}>{numberToGrade(p.contact)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.speed) }}>{numberToGrade(p.speed)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.fielding) }}>{numberToGrade(p.fielding)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span className="stamina-mini"><span className="stamina-mini-bar"><span className="stamina-mini-fill" style={{ width: `${p.current_stamina || 100}%`, background: (p.current_stamina || 100) >= 60 ? '#00d4aa' : (p.current_stamina || 100) >= 35 ? '#f59e0b' : '#ef4444' }} /></span>{p.current_stamina || 100}</span></td>
                  <td><button className="release-player-btn" onClick={async (e) => { e.stopPropagation(); if (confirm(`Release ${p.first_name} ${p.last_name}?`)) { await supabase!.from('players').update({ team_id: null }).eq('id', p.id); setRoster(prev => prev.filter(r => r.id !== p.id)); } }} title="Release">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="roster-section">
          <h3>Pitchers ({pitchers.length})</h3>
          <table className="roster-table">
            <thead><tr><th>Name</th><th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'position', setPitcherSort)}>Role{sortArrow(pitcherSort, 'position')}</th><th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'overall', setPitcherSort)}>OVR{sortArrow(pitcherSort, 'overall')}</th><th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'velocity', setPitcherSort)}>VEL{sortArrow(pitcherSort, 'velocity')}</th><th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'control', setPitcherSort)}>CTL{sortArrow(pitcherSort, 'control')}</th><th className="sortable-th" onClick={() => toggleSort(pitcherSort, 'movement', setPitcherSort)}>MOV{sortArrow(pitcherSort, 'movement')}</th><th>STA</th><th></th></tr></thead>
            <tbody>
              {sortedPitchers.map(p => (
                <tr key={p.id} className="roster-row">
                  <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}>{depthChartPos[p.id] || p.position}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)} style={{ color: getAttrColor(p.overall) }}>{numberToGrade(p.overall)}</td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.velocity) }}>{numberToGrade(p.velocity)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.control) }}>{numberToGrade(p.control)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span style={{ color: getAttrColor(p.movement) }}>{numberToGrade(p.movement)}</span></td>
                  <td onClick={() => onNavigate?.('/player/' + p.id)}><span className="stamina-mini"><span className="stamina-mini-bar"><span className="stamina-mini-fill" style={{ width: `${p.current_stamina || 100}%`, background: (p.current_stamina || 100) >= 60 ? '#00d4aa' : (p.current_stamina || 100) >= 35 ? '#f59e0b' : '#ef4444' }} /></span>{p.current_stamina || 100}</span></td>
                  <td><button className="release-player-btn" onClick={async (e) => { e.stopPropagation(); if (confirm(`Release ${p.first_name} ${p.last_name}?`)) { await supabase!.from('players').update({ team_id: null }).eq('id', p.id); setRoster(prev => prev.filter(r => r.id !== p.id)); } }} title="Release">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {myFreeAgents.length > 0 && (
          <div className="fa-sign-banner">
            <div className="fa-banner-left">
              <span className="fa-banner-icon">✍️</span>
              <div className="fa-banner-text">
                <strong>{myFreeAgents.length} of your player{myFreeAgents.length > 1 ? 's are' : ' is'} a free agent!</strong>
                <span>Sign them to {selectedTeam?.city} {selectedTeam?.name} to fill your roster.</span>
              </div>
            </div>
            <div className="fa-banner-actions">
              <button className="fa-sign-all-btn" onClick={async () => {
                if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;
                if (!confirm(`Sign ${myFreeAgents.length} player${myFreeAgents.length > 1 ? 's' : ''} to ${selectedTeam.city} ${selectedTeam.name}? Lowest scrubs will be released if roster is full.`)) return;
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) { alert('Not signed in'); return; }
                let signed = 0;
                let dropped = 0;
                for (const p of myFreeAgents) {
                  const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                    body: JSON.stringify({ action: 'sign-player', playerId: p.id, teamId: selectedTeam.id }),
                  });
                  const data = await res.json();
                  if (data.success) {
                    signed++;
                    if (data.droppedScrub) dropped++;
                  } else {
                    alert(`Error signing ${p.first_name} ${p.last_name}: ${data.error}`);
                    break;
                  }
                }
                let msg = `Signed ${signed} player${signed > 1 ? 's' : ''}.`;
                if (dropped > 0) msg += ` ${dropped} scrub${dropped > 1 ? 's' : ''} released to FA.`;
                if (signed > 0) alert(msg);
                setMyFreeAgents([]);
                loadTeamData();
              }}>
                Sign All ({myFreeAgents.length})
              </button>
            </div>
          </div>
        )}
        {myFreeAgents.length > 0 && (
          <div className="roster-section fa-sign-section">
            <h3 className="fa-sign-header">
              ✍️ Your Free Agents
              {myFreeAgents.length > 0 && <span className="fa-count-badge">{myFreeAgents.length}</span>}
            </h3>
            {myFreeAgents.length === 0 ? (
              <p className="fa-empty-msg">No unsigned players. Create more at <span style={{ color: '#e8b82a', cursor: 'pointer' }} onClick={() => onNavigate?.('/create-player')}>Create Player</span>.</p>
            ) : (
              <table className="roster-table fa-sign-table">
                <thead><tr><th>Name</th><th>Pos</th><th>OVR</th><th></th></tr></thead>
                <tbody>
                  {myFreeAgents.map(p => (
                    <tr key={p.id} className="roster-row fa-sign-row">
                      <td className="player-name-cell">
                        {p.first_name} {p.last_name}
                        {faOfferCounts[p.id] > 0 && (
                          <span className="fa-offer-badge" title={`${faOfferCounts[p.id]} pending offer${faOfferCounts[p.id] > 1 ? 's' : ''}`}>
                            📩 {faOfferCounts[p.id]}
                          </span>
                        )}
                      </td>
                      <td>{p.position}</td>
                      <td style={{ color: getAttrColor(p.overall) }}>{numberToGrade(p.overall)}</td>
                      <td><button className="sign-player-btn" onClick={async () => {
                        if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session) { alert('Not signed in'); return; }
                        const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                          body: JSON.stringify({ action: 'sign-player', playerId: p.id, teamId: selectedTeam.id }),
                        });
                        const data = await res.json();
                        if (!data.success) {
                          alert('Error signing player: ' + (data.error || 'Unknown error'));
                          return;
                        }
                        if (data.droppedScrub) {
                          // Toast or subtle notice — scrub was released
                          console.log('Roster adjustment:', data.droppedScrub);
                        }
                        setMyFreeAgents(prev => prev.filter(fa => fa.id !== p.id));
                        loadTeamData();
                      }}>Sign</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderLineup = () => {
    return (
      <div className="lineup-tab">
        <div className="lineup-section">
          <h3>Batting Order</h3>
          <table className="lineup-table">
            <thead><tr><th>#</th><th></th><th>Player</th><th>Pos</th><th>OVR</th></tr></thead>
            <tbody>
              {lineup.map((pid, idx) => {
                const p = getPlayer(pid);
                if (!p) return null;
                return (
                  <tr key={pid}>
                    <td className="order-num">{idx + 1}</td>
                    <td className="reorder-cell">
                      {idx > 0 && <button className="reorder-btn" onClick={() => { const nl = [...lineup]; [nl[idx-1], nl[idx]] = [nl[idx], nl[idx-1]]; setLineup(nl); }}>▲</button>}
                      {idx < lineup.length - 1 && <button className="reorder-btn" onClick={() => { const nl = [...lineup]; [nl[idx], nl[idx+1]] = [nl[idx+1], nl[idx]]; setLineup(nl); }}>▼</button>}
                    </td>
                    <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                    <td>{depthChartPos[p.id] || p.position}</td>
                    <td style={{ color: getAttrColor(p.overall) }}>{numberToGrade(p.overall)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="lineup-section">
          <h3>Pitching Rotation</h3>
          <table className="lineup-table">
            <thead><tr><th>#</th><th></th><th>Player</th><th>Role</th><th>OVR</th><th>VEL</th><th>CTL</th><th>MOV</th></tr></thead>
            <tbody>
              {pitchingRotation.map((pid, idx) => {
                const p = getPlayer(pid);
                if (!p) return null;
                return (
                  <tr key={pid}>
                    <td className="order-num">P{idx + 1}</td>
                    <td className="reorder-cell">
                      {idx > 0 && <button className="reorder-btn" onClick={() => { const pr = [...pitchingRotation]; [pr[idx-1], pr[idx]] = [pr[idx], pr[idx-1]]; setPitchingRotation(pr); }}>▲</button>}
                      {idx < pitchingRotation.length - 1 && <button className="reorder-btn" onClick={() => { const pr = [...pitchingRotation]; [pr[idx], pr[idx+1]] = [pr[idx+1], pr[idx]]; setPitchingRotation(pr); }}>▼</button>}
                    </td>
                    <td className="player-name-cell" onClick={() => onNavigate?.('/player/' + p.id)}>{p.first_name} {p.last_name}</td>
                    <td style={{ color: '#60a5fa', fontSize: 13, fontWeight: 600 }}>{depthChartPos[p.id] || `P${idx + 1}`}</td>
                    <td style={{ color: getAttrColor(p.overall) }}>{numberToGrade(p.overall)}</td>
                    <td><span style={{ color: getAttrColor(p.velocity) }}>{numberToGrade(p.velocity)}</span></td>
                    <td><span style={{ color: getAttrColor(p.control) }}>{numberToGrade(p.control)}</span></td>
                    <td><span style={{ color: getAttrColor(p.movement) }}>{numberToGrade(p.movement)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button className="save-btn" style={{ marginTop: 12 }} onClick={async () => {
            if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;
            const spIds = pitchingRotation.slice(0, 5);
            // Convert depthChart state to position → [player_id, ...] for DB
            const dcIds: Record<string, string[]> = {};
            Object.keys(depthChart).forEach(pos => {
              dcIds[pos] = (depthChart[pos] || []).filter((id): id is string => id !== null);
            });
            await supabase.from('teams').update({ rotation_order: spIds, batting_order: lineup, depth_chart: dcIds }).eq('id', selectedTeam.id);
            alert('✅ Lineup, rotation, and depth chart saved!');
          }}>💾 Save Lineup, Rotation & Depth Chart</button>
        </div>
        <div className="lineup-section">
          <h3>Depth Chart — Position Players</h3>
          <p className="depth-chart-note">Set 3-deep depth per position. Backups replace tired starters automatically.</p>
          <table className="depth-chart-table dc-dropdown-table">
            <thead><tr><th>Pos</th><th>1st</th><th>2nd</th><th>3rd</th></tr></thead>
            <tbody>
              {POSITIONS.map(pos => {
                const eligible = roster.filter(p => p.is_hitter && p.position === pos);
                const utility = roster.filter(p => p.is_hitter && p.position !== pos);
                const allOptions = [...eligible, ...utility].filter((p, i, a) => a.findIndex(x => x.id === p.id) === i).sort((a, b) => b.overall - a.overall);

                const slotIds = depthChart[pos] || [null, null, null];
                const getAvailableForSlot = (slotIdx: number) => {
                  const taken = slotIds.filter((id, i) => i !== slotIdx && id !== null) as string[];
                  return allOptions.filter(p => !taken.includes(p.id));
                };

                return (
                  <tr key={pos} className="dc-hitter-row">
                    <td className="pos-cell dc-pos-cell">{pos}</td>
                    {[0, 1, 2].map(slotIdx => {
                      const available = getAvailableForSlot(slotIdx);
                      const selectedId = slotIds[slotIdx];
                      return (
                        <td key={slotIdx} className="dc-slot-cell">
                          <select
                            className="dc-dropdown"
                            value={selectedId || ''}
                            onChange={e => {
                              const newId = e.target.value || null;
                              setDepthChart(prev => {
                                const updated = { ...prev };
                                const slots = [...(updated[pos] || [null, null, null])];
                                slots[slotIdx] = newId;
                                updated[pos] = slots;
                                return updated;
                              });
                            }}
                          >
                            <option value="">— Empty —</option>
                            {available.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.first_name} {p.last_name} ({p.position} {p.overall})
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="lineup-section">
          <h3>Pitching Rotation</h3>
          <p className="depth-chart-note">Assign pitchers by overall rank. Top 5 are starting rotation, rest are bullpen.</p>
          <table className="depth-chart-table dc-dropdown-table">
            <thead><tr><th>Rank</th><th>Pitcher</th></tr></thead>
            <tbody>
              {Array.from({ length: 11 }, (_, idx) => {
                const key = `P${idx + 1}`;
                const ROLE_LABELS: Record<number, string> = {1:'SP1',2:'SP2',3:'SP3',4:'SP4',5:'SP5',6:'LR',7:'MR1',8:'MR2',9:'SU1',10:'SU2',11:'CL'};
                const allPitchers = roster.filter(p => !p.is_hitter).sort((a, b) => b.overall - a.overall);
                const slotIds = depthChart[key] || [null, null, null];

                return (
                  <tr key={key} className="dc-pitcher-row">
                    <td className="pos-cell dc-pos-cell dc-role-cell">{ROLE_LABELS[idx + 1]}</td>
                    <td className="dc-slot-cell dc-role-slot">
                      <select
                        className="dc-dropdown"
                        value={slotIds[0] || ''}
                        onChange={e => {
                          const newId = e.target.value || null;
                          setDepthChart(prev => {
                            const updated = { ...prev };
                            updated[key] = [newId, null, null];
                            return updated;
                          });
                        }}
                      >
                        <option value="">— Empty —</option>
                        {allPitchers.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.first_name} {p.last_name} ({p.position} {p.overall})
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPlayerStats = () => (
    <div className="player-stats-tab">
      <h3>Individual Player Stats</h3>
      <p className="section-note">Click a player for full stats and game log.</p>
      <table className="player-stats-table">
        <thead><tr><th>Player</th><th>Pos</th><th>G</th><th>AB</th><th>H</th><th>HR</th><th>RBI</th><th>AVG</th></tr></thead>
        <tbody>
          {roster.filter(p => p.is_hitter).map(p => {
            const s = playerStats.get(p.id);
            const g = s?.games || 0;
            const ab = s?.at_bats || 0;
            const h = s?.hits || 0;
            const hr = s?.home_runs || 0;
            const rbi = s?.rbi || 0;
            const avg = ab > 0 ? (h / ab).toFixed(3).replace(/^0/, '') : '—';
            return (
              <tr key={p.id} className="roster-row" onClick={() => onNavigate?.('/player/' + p.id)}>
                <td className="player-name-cell">{p.first_name} {p.last_name}</td><td>{p.position}</td>
                <td>{g || '—'}</td><td>{ab || '—'}</td><td>{h || '—'}</td>
                <td>{hr || '—'}</td><td>{rbi || '—'}</td><td>{avg}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <table className="player-stats-table" style={{ marginTop: 20 }}>
        <thead><tr><th>Player</th><th>Role</th><th>G</th><th>W</th><th>L</th><th>ERA</th><th>K</th></tr></thead>
        <tbody>
          {roster.filter(p => !p.is_hitter).map(p => {
            const s = playerStats.get(p.id);
            const g = s?.games || 0;
            const w = s?.wins || 0;
            const l = s?.losses || 0;
            const ip = s?.innings_pitched || 0;
            const er = s?.earned_runs || 0;
            const era = ip > 0 ? ((er * 9) / ip).toFixed(2) : '—';
            const so = s?.strikeouts_pitched || 0;
            return (
              <tr key={p.id} className="roster-row" onClick={() => onNavigate?.('/player/' + p.id)}>
                <td className="player-name-cell">{p.first_name} {p.last_name}</td><td>{p.position}</td>
                <td>{g || '—'}</td><td>{w || '—'}</td><td>{l || '—'}</td>
                <td>{era}</td><td>{so || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTeamStats = () => {
    const total = (selectedTeam?.wins || 0) + (selectedTeam?.losses || 0);
    return (
      <div className="team-stats-tab">
        <h3>Team Statistics</h3>
        <table className="team-stats-table">
          <thead><tr><th>Stat</th><th>Value</th></tr></thead>
          <tbody>
            <tr><td>Wins</td><td>{selectedTeam?.wins || 0}</td></tr>
            <tr><td>Losses</td><td>{selectedTeam?.losses || 0}</td></tr>
            <tr><td>Win %</td><td>{total > 0 ? ((selectedTeam?.wins || 0) / total).toFixed(3) : '.000'}</td></tr>
            <tr><td>Runs Scored</td><td>{(selectedTeam as any)?.runs_scored || 0}</td></tr>
            <tr><td>Runs Allowed</td><td>{(selectedTeam as any)?.runs_allowed || 0}</td></tr>
            <tr><td>Run Differential</td><td>{((selectedTeam as any)?.runs_scored || 0) - ((selectedTeam as any)?.runs_allowed || 0)}</td></tr>
            <tr><td>Roster Size</td><td>{roster.length} / 25</td></tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderSchedule = () => {
    let runningW = 0, runningL = 0;
    const navigate = (page: string) => window.dispatchEvent(new CustomEvent('navigate', { detail: page }));

    return (
      <div className="schedule-tab">
        <table className="bh-schedule-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Opponent</th>
              <th>Result</th>
              <th>Record</th>
              <th>Win</th>
              <th>Loss</th>
              <th>Save</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map(game => {
              const isHome = game.home_team_id === selectedTeam?.id;
              const oppTeam = (game as any).opp_team;
              const oppName = oppTeam?.city && oppTeam?.name ? `${oppTeam.city} ${oppTeam.name}` : 'TBD';
              const oppId = (game as any).opp_id || (isHome ? game.away_team_id : game.home_team_id);
              const isCompleted = game.status === 'completed';

              let result = '', resultClass = '', score = '';
              if (isCompleted) {
                const myScore = isHome ? game.home_score : game.away_score;
                const theirScore = isHome ? game.away_score : game.home_score;
                const won = (myScore || 0) > (theirScore || 0);
                if (won) runningW++; else runningL++;
                result = won ? 'W' : 'L';
                resultClass = won ? 'result-w' : 'result-l';
                score = `${myScore}–${theirScore}`;
              }

              // Off day
              if (!isCompleted && !game.home_team) {
                return (
                  <tr key={game.id} className="off-day">
                    <td>{gameDayToShortDate(game.game_day)}</td>
                    <td colSpan={6} className="off-day-cell">—</td>
                  </tr>
                );
              }

              return (
                <tr key={game.id}>
                  <td className="date-cell">{gameDayToShortDate(game.game_day)}</td>
                  <td>
                    <span className="ha-cell">{isHome ? 'vs' : '@'}</span>{' '}
                    <span className="opp-link" onClick={() => onNavigate?.(`/team/${oppId}`)}>{oppName}</span>
                  </td>
                  <td>
                    {isCompleted ? (
                      <span className={resultClass}>
                        <span className="score-link" onClick={() => setBoxscoreGameId(game.game_id || game.id)}>{result}, {score}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="record-cell">{isCompleted ? `${runningW}–${runningL}` : '—'}</td>
                  <td>
                    {game.win_pitcher ? (
                      <span className="player-link" onClick={() => navigate('/player/' + game.win_pitcher?.id)}>{game.win_pitcher.name}</span>
                    ) : '—'}
                  </td>
                  <td>
                    {game.loss_pitcher ? (
                      <span className="player-link" onClick={() => navigate('/player/' + game.loss_pitcher?.id)}>{game.loss_pitcher.name}</span>
                    ) : '—'}
                  </td>
                  <td>
                    {game.save_pitcher ? (
                      <span className="player-link" onClick={() => navigate('/player/' + game.save_pitcher?.id)}>{game.save_pitcher.name}</span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
            {schedule.length === 0 && <tr><td colSpan={7} className="empty-cell">No games scheduled.</td></tr>}
          </tbody>
        </table>
      </div>
    );
  };

  const renderOffers = () => {
    const pending = receivedOffers.filter(o => o.status === 'pending');
    const past = receivedOffers.filter(o => o.status !== 'pending');
    const sentPending = sentOffers.filter(o => o.status === 'pending');
    const sentPast = sentOffers.filter(o => o.status !== 'pending');

    return (
      <div className="offers-tab">
        {/* RECEIVED OFFERS */}
        <div className="offers-section">
          <h3 className="offers-section-title">📩 Offers Received</h3>
          {pending.length === 0 ? (
            <p className="offers-empty">No pending contract offers for your free agents</p>
          ) : (
            <div className="offers-list">
              {pending.map(o => (
                <div key={o.id} className="offer-card">
                  <div className="offer-card-header">
                    <span className="offer-player-name">{o.player_first_name} {o.player_last_name}</span>
                    <span className="offer-player-pos" style={{ color: getAttrColor(o.player_overall) }}>{o.player_position} {o.player_overall}</span>
                  </div>
                  <div className="offer-card-body">
                    <div className="offer-detail">
                      <span className="offer-label">From</span>
                      <span className="offer-value">{o.team_city} {o.team_name}</span>
                    </div>
                    <div className="offer-detail">
                      <span className="offer-label">Expires</span>
                      <span className="offer-value">{new Date(o.expires_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="offer-card-actions">
                    <button
                      className="offer-accept-btn"
                      disabled={acceptingOfferId === o.id}
                      onClick={() => {
                        if (confirm(`Accept offer from ${o.team_city} ${o.team_name}?`)) {
                          handleAcceptOffer(o.id);
                        }
                      }}
                    >
                      {acceptingOfferId === o.id ? 'Accepting...' : '✓ Accept Offer'}
                    </button>
                    <button
                      className="offer-decline-btn"
                      onClick={() => {
                        if (confirm(`Decline offer from ${o.team_city} ${o.team_name}?`)) {
                          handleDeclineOffer(o.id);
                        }
                      }}
                    >
                      ✕ Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {past.length > 0 && (
          <div className="offers-section offers-past">
            <h3 className="offers-section-title">Past Received</h3>
            <div className="offers-list">
              {past.map(o => (
                <div key={o.id} className="offer-card offer-card-past">
                  <div className="offer-card-header">
                    <span className="offer-player-name">{o.player_first_name} {o.player_last_name}</span>
                    <span className={`offer-status offer-status-${o.status}`}>{o.status}</span>
                  </div>
                  <div className="offer-card-body">
                    <div className="offer-detail">
                      <span className="offer-label">From</span>
                      <span className="offer-value">{o.team_city} {o.team_name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SENT OFFERS */}
        <div className="offers-section" style={{ marginTop: '24px' }}>
          <h3 className="offers-section-title">📤 Offers Sent</h3>
          {sentPending.length === 0 ? (
            <p className="offers-empty">No pending offers sent by your teams</p>
          ) : (
            <div className="offers-list">
              {sentPending.map(o => (
                <div key={o.id} className="offer-card">
                  <div className="offer-card-header">
                    <span className="offer-player-name">
                      {o.player_avatar ? <img src={o.player_avatar} alt="" className="fa-row-portrait" /> : null}
                      {o.player_first_name} {o.player_last_name}
                    </span>
                    <span className="offer-player-pos" style={{ color: getAttrColor(o.player_overall) }}>{o.player_position} {o.player_overall}</span>
                  </div>
                  <div className="offer-card-body">
                    <div className="offer-detail">
                      <span className="offer-label">To Player</span>
                      <span className="offer-value">{o.player_first_name} {o.player_last_name} ({o.player_position} {o.player_overall})</span>
                    </div>
                    <div className="offer-detail">
                      <span className="offer-label">Expires</span>
                      <span className="offer-value">{new Date(o.expires_at).toLocaleDateString()}</span>
                    </div>
                    {/* Competing offers from other teams */}
                    {sentCompetingOffers[o.player_id] && sentCompetingOffers[o.player_id].length > 0 && (
                      <div className="competing-offers-section">
                        <div className="competing-offers-title">
                          ⚔️ {sentCompetingOffers[o.player_id].length} Competing Offer{sentCompetingOffers[o.player_id].length > 1 ? 's' : ''}
                        </div>
                        {sentCompetingOffers[o.player_id].map((c: any) => (
                          <div key={c.id} className="competing-offer-row">
                            <span className="competing-team">{c.team_city} {c.team_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="offer-card-actions">
                    <button
                      className="offer-decline-btn"
                      disabled={withdrawingOfferId === o.id}
                      onClick={() => {
                        if (confirm(`Withdraw your offer to ${o.player_first_name} ${o.player_last_name}?`)) {
                          handleWithdrawOffer(o.id);
                        }
                      }}
                    >
                      {withdrawingOfferId === o.id ? 'Withdrawing...' : '✕ Withdraw Offer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {sentPast.length > 0 && (
          <div className="offers-section offers-past">
            <h3 className="offers-section-title">Past Sent</h3>
            <div className="offers-list">
              {sentPast.map(o => (
                <div key={o.id} className="offer-card offer-card-past">
                  <div className="offer-card-header">
                    <span className="offer-player-name">{o.player_first_name} {o.player_last_name}</span>
                    <span className={`offer-status offer-status-${o.status}`}>{o.status}</span>
                  </div>
                  <div className="offer-card-body">
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBoosts = () => {
    const unequipped = myBoosts.filter((b: any) => !b.is_equipped);
    const equipped = myBoosts.filter((b: any) => b.is_equipped);
    const canForge = forgeSelection.length === 2;
    const forgeBoost1 = myBoosts.find((b: any) => b.id === forgeSelection[0]);
    const forgeBoost2 = myBoosts.find((b: any) => b.id === forgeSelection[1]);

    return (
      <div className="boosts-tab">
        <h3>⚡ Boosts</h3>
        <p className="section-note">Equip boosts to your players for attribute bonuses. ONE boost per player. ONE equip per week per boost. Combine 2 same-attr same-rank boosts in the Forge to upgrade.</p>

        {/* Forge */}
        <div className="boosts-forge" style={{ background: 'rgba(200,150,26,0.08)', border: '1px solid rgba(200,150,26,0.2)', borderRadius: 12, padding: 16, marginBottom: 24 }}>
          <h4 style={{ color: '#e8b82a', marginBottom: 8 }}>🔥 Forge — Combine 2 Boosts</h4>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>Select 2 boosts of the same attribute and rank to forge a higher-rank boost (2:1 ratio).</p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 120, padding: 12, background: 'rgba(16,24,40,0.6)', borderRadius: 8, textAlign: 'center' }}>
              {forgeBoost1 ? (
                <div>
                  <span style={{ fontSize: 18 }}>{forgeBoost1.boost_definitions?.icon || '⚡'}</span>
                  <div style={{ color: '#f0ede6', fontSize: 13 }}>{forgeBoost1.boost_definitions?.name}</div>
                </div>
              ) : (
                <span style={{ color: '#6b7280' }}>Select 1st</span>
              )}
            </div>
            <span style={{ color: '#e8b82a', fontSize: 20 }}>+</span>
            <div style={{ flex: 1, minWidth: 120, padding: 12, background: 'rgba(16,24,40,0.6)', borderRadius: 8, textAlign: 'center' }}>
              {forgeBoost2 ? (
                <div>
                  <span style={{ fontSize: 18 }}>{forgeBoost2.boost_definitions?.icon || '⚡'}</span>
                  <div style={{ color: '#f0ede6', fontSize: 13 }}>{forgeBoost2.boost_definitions?.name}</div>
                </div>
              ) : (
                <span style={{ color: '#6b7280' }}>Select 2nd</span>
              )}
            </div>
            <span style={{ color: '#e8b82a', fontSize: 20 }}>→</span>
            <button
              className="offer-accept-btn"
              disabled={!canForge || boostLoading}
              onClick={handleForgeCombine}
              style={{ minWidth: 100 }}
            >
              {boostLoading ? 'Forging...' : '🔥 Forge'}
            </button>
          </div>
          {forgeResult && (
            <div style={{ marginTop: 12, padding: 10, background: 'rgba(34,197,94,0.1)', borderRadius: 8, color: '#22c55e' }}>
              ✅ Forged: {forgeResult.name} (+{forgeResult.boostValue})
            </div>
          )}
        </div>

        {/* Equipped Boosts */}
        {equipped.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ color: '#f0ede6', marginBottom: 8 }}>✅ Active Boosts</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {equipped.map((b: any) => {
                const def = b.boost_definitions;
                const player = roster.find((p: any) => p.id === b.player_id);
                return (
                  <div key={b.id} style={{ background: 'rgba(16,24,40,0.6)', border: '1px solid rgba(156,163,175,0.2)', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>{def?.icon || '⚡'}</span>
                    </div>
                    <div style={{ color: '#f0ede6', fontSize: 14, fontWeight: 600, margin: '4px 0' }}>{def?.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>→ {player ? `${player.first_name} ${player.last_name}` : 'Unknown'}</div>
                    <button
                      className="offer-decline-btn"
                      style={{ marginTop: 8, fontSize: 11, padding: '4px 10px' }}
                      disabled={boostLoading}
                      onClick={() => handleUnequipBoost(b.id)}
                    >
                      Unequip
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Inventory */}
        <div>
          <h4 style={{ color: '#f0ede6', marginBottom: 8 }}>🎒 Inventory ({unequipped.length})</h4>
          {unequipped.length === 0 ? (
            <p style={{ color: '#6b7280', fontSize: 14 }}>No boosts in inventory. Earn boosts from login bonuses and loot boxes!</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {unequipped.map((b: any) => {
                const def = b.boost_definitions;
                const isSelected = forgeSelection.includes(b.id);
                return (
                  <div
                    key={b.id}
                    style={{
                      background: isSelected ? 'rgba(200,150,26,0.15)' : 'rgba(16,24,40,0.6)',
                      border: `1px solid ${isSelected ? '#e8b82a' : 'rgba(156,163,175,0.2)'}`,
                      borderRadius: 10,
                      padding: 12,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onClick={() => {
                      if (isSelected) {
                        setForgeSelection(prev => prev.filter(id => id !== b.id));
                      } else if (forgeSelection.length < 2) {
                        setForgeSelection(prev => [...prev, b.id]);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>{def?.icon || '⚡'}</span>
                    </div>
                    <div style={{ color: '#f0ede6', fontSize: 14, fontWeight: 600, margin: '4px 0' }}>{def?.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      {def?.boost_type === 'attribute' ? `+${def.boost_value} ${def.target_attribute}` :
                       def?.boost_type === 'stamina' ? `+${def.boost_value} stamina recovery` :
                       def?.boost_type === 'special' ? `+${def.boost_value} all attrs` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button
                        className="offer-accept-btn"
                        style={{ fontSize: 11, padding: '4px 10px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEquipModalBoost(b);
                        }}
                      >
                        Equip
                      </button>
                      <span style={{ fontSize: 11, color: '#6b7280', lineHeight: '24px' }}>
                        {isSelected ? '✓ Forge' : 'Tap to select for forge'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Equip Modal */}
        {equipModalBoost && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }} onClick={() => setEquipModalBoost(null)}>
            <div style={{
              background: '#0a0e17', border: '1px solid rgba(200,150,26,0.3)', borderRadius: 16,
              padding: 24, maxWidth: 400, width: '90%',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ color: '#f0ede6', marginBottom: 16 }}>
                Equip {equipModalBoost.boost_definitions?.name || 'Boost'}
              </h3>
              <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                Select a player to equip this boost to. This will unequip any existing boost on that player.
              </p>
              <select
                value={equipModalPlayer || ''}
                onChange={e => setEquipModalPlayer(e.target.value)}
                style={{ width: '100%', padding: 10, background: '#060a12', color: '#f0ede6', border: '1px solid rgba(200,150,26,0.3)', borderRadius: 8, marginBottom: 16 }}
              >
                <option value="">Choose a player...</option>
                {roster.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.position} {p.overall})</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="offer-accept-btn" disabled={!equipModalPlayer || boostLoading}
                  onClick={() => equipModalPlayer && handleEquipBoost(equipModalBoost.id, equipModalPlayer)}>
                  {boostLoading ? 'Equipping...' : '✓ Equip'}
                </button>
                <button className="offer-decline-btn" onClick={() => setEquipModalBoost(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStrategy = () => {
    const sliderGroups: { group: string; items: { key: keyof StrategySettings; label: string; left: string; right: string; desc: string }[] }[] = [
      {
        group: '⚔️ Offense',
        items: [
          { key: 'hittingApproach', label: 'Hitting Approach', left: 'Small Ball', right: 'Swing for Power', desc: 'Contact & singles vs swinging for extra bases. Affects exit velocity and HR rate.' },
          { key: 'stealAttempts', label: 'Steal Attempts', left: 'Rare', right: 'Run Wild', desc: 'How aggressively runners attempt to steal. Higher means more attempts — and more caught stealings.' },
          { key: 'baseRunningAggression', label: 'Base Running', left: 'Conservative', right: 'Aggressive', desc: 'How often runners take extra bases on hits — scoring from 2nd on singles, stretching doubles into triples.' },
          { key: 'sacrificeBunts', label: 'Sacrifice Bunts', left: 'Never', right: 'Often', desc: 'How often to bunt runners into scoring position. More effective with slow power hitters at the plate.' },
          { key: 'intentionalWalks', label: 'Intentional Walks', left: 'Never', right: 'Pitch Around', desc: 'How often to put dangerous hitters on base intentionally. Only triggers for high-power batters with runners on.' },
        ],
      },
      {
        group: '🛡️ Pitching & Defense',
        items: [
          { key: 'pitcherManagement', label: 'Pitcher Hook', left: 'Quick Hook', right: 'Long Leash', desc: 'How quickly you pull struggling starters. Quick hook means fresher arms; long leash risks more damage.' },
          { key: 'bullpenUsage', label: 'Bullpen Usage', left: 'Save for 9th', right: 'Best Arm Now', desc: 'When to use your best relievers. High = bring in your ace bullpen arm in the 6th inning if needed.' },
          { key: 'shiftFrequency', label: 'Defensive Shift', left: 'Standard', right: 'Shift Heavy', desc: 'How often to overload the pull side against power hitters. Reduces grounder hits but opens the opposite field.' },
        ],
      },
      {
        group: '📋 Lineup Philosophy',
        items: [
          { key: 'defenseFirst', label: 'Defense Priority', left: 'Bat First', right: 'Defense First', desc: 'Whether to prioritize fielding over offense in lineup construction. High = field best gloves even if they hit less.' },
          { key: 'lineupProtection', label: 'Lineup Protection', left: 'Spread Out', right: 'Cluster Stars', desc: 'Concentrate your best hitters in the 3-4-5 spots (high) vs spread production across the lineup (low).' },
        ],
      },
    ];
    return (
      <div className="strategy-tab">
        <h3>Team Strategy</h3>
        <p className="section-note">These sliders directly affect how your team plays in every simulated game. Experiment and find your style.</p>
        {sliderGroups.map(group => (
          <div key={group.group} className="strategy-group">
            <h4 className="strategy-group-label">{group.group}</h4>
            <div className="strategy-sliders">
              {group.items.map(s => (
                <div key={s.key} className="strategy-slider-row">
                  <div className="slider-header">
                    <span className="slider-label">{s.label}</span>
                    <span className="slider-value">{strategy[s.key] ?? 50}</span>
                  </div>
                  <div className="slider-desc">{s.desc}</div>
                  <div className="slider-track-wrapper">
                    <span className="slider-endpoint">{s.left}</span>
                    <input type="range" min="0" max="100" value={strategy[s.key] ?? 50}
                      onChange={e => setStrategy(prev => ({ ...prev, [s.key]: parseInt(e.target.value) }))}
                      className="strategy-range" />
                    <span className="slider-endpoint">{s.right}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <button className="save-strategy-btn" onClick={handleSaveStrategy}>Save Strategy</button>
      </div>
    );
  };

  const renderUniforms = () => (
    <div className="uniforms-tab">
      <h3>Uniform Customizer</h3>
      <p className="section-note">Design your team's look.</p>

      {/* Preview */}
      {renderUniformPreview()}

      {/* Style selector */}
      <div className="uniform-section">
        <h4>Jersey Style</h4>
        <div className="style-options">
          {UNIFORM_STYLES.map(s => (
            <button key={s.value}
              className={`style-option ${uniform.style === s.value ? 'active' : ''}`}
              onClick={() => setUniform(prev => ({ ...prev, style: s.value as any }))}
            >
              <span className="style-label">{s.label}</span>
              <span className="style-desc">{s.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Primary color */}
      <div className="uniform-section">
        <h4>Primary Color</h4>
        <div className="color-swatches">
          {TEAM_COLORS.map(c => (
            <button key={c} className={`swatch ${uniform.primaryColor === c ? 'active' : ''}`}
              style={{ background: c }} onClick={() => setUniform(prev => ({ ...prev, primaryColor: c }))} />
          ))}
          <label className="swatch custom-swatch">
            <input type="color" value={uniform.primaryColor} onChange={e => setUniform(prev => ({ ...prev, primaryColor: e.target.value }))} />
            <span>+</span>
          </label>
        </div>
      </div>

      {/* Secondary color */}
      <div className="uniform-section">
        <h4>Secondary Color</h4>
        <div className="color-swatches">
          {TEAM_COLORS.map(c => (
            <button key={c} className={`swatch ${uniform.secondaryColor === c ? 'active' : ''}`}
              style={{ background: c }} onClick={() => setUniform(prev => ({ ...prev, secondaryColor: c }))} />
          ))}
          <label className="swatch custom-swatch">
            <input type="color" value={uniform.secondaryColor} onChange={e => setUniform(prev => ({ ...prev, secondaryColor: e.target.value }))} />
            <span>+</span>
          </label>
        </div>
      </div>

      {/* Cap color */}
      <div className="uniform-section">
        <h4>Cap Color</h4>
        <div className="color-swatches">
          {TEAM_COLORS.map(c => (
            <button key={c} className={`swatch ${uniform.capColor === c ? 'active' : ''}`}
              style={{ background: c }} onClick={() => setUniform(prev => ({ ...prev, capColor: c }))} />
          ))}
          <label className="swatch custom-swatch">
            <input type="color" value={uniform.capColor} onChange={e => setUniform(prev => ({ ...prev, capColor: e.target.value }))} />
            <span>+</span>
          </label>
        </div>
      </div>

      <button className="save-strategy-btn" onClick={handleSaveUniform}>Save Uniform</button>
    </div>
  );

  if (loading) return <div className="my-teams-page"><div className="loading">Loading...</div></div>;

  // No team selected — show team list
  if (!selectedTeam) {
    return (
      <div className="my-teams-page">
        <div className="my-teams-header">
          <h1>Team Owner's Office</h1>
          <p className="subtitle">{userData?.username ? `Teams owned by ${userData.username}` : 'Your teams'}</p>
          {userBalance > 0 && <span className="balls-balance">{userBalance.toLocaleString()} $BALLS</span>}
        </div>

        {ownedTeams.length > 0 && (
          <div className="my-teams-grid">
            {ownedTeams.map(team => {
              const total = team.wins + team.losses;
              const pct = total > 0 ? (team.wins / total).toFixed(3) : '.000';
              return (
                <div key={team.id} className="my-team-card" onClick={() => setSelectedTeam(team)}>
                  <div className="team-card-abbr" style={{ background: team.primary_color || 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}>{team.abbreviation}</div>
                  <div className="team-card-info">
                    <h3>{team.city} {team.name}</h3>
                    <div className="team-card-record">{team.wins}-{team.losses} ({pct})</div>
                  </div>
                  <span className="team-card-arrow">→</span>
                </div>
              );
            })}
          </div>
        )}

        {queuedTeams.length > 0 && (
          <div style={{marginTop: 16, marginBottom: 16}}>
            <h3 style={{color: '#9ca3af', fontSize: '0.85rem', marginBottom: 8}}>⏳ Queued for Next Season</h3>
            <div className="my-teams-grid">
              {queuedTeams.map(qt => (
                <div key={qt.id} className="my-team-card" style={{opacity: 0.7, border: '1px dashed #444'}}>
                  <div className="team-card-abbr" style={{ background: '#374151' }}>{qt.abbreviation || '?'}</div>
                  <div className="team-card-info">
                    <h3>{qt.city ? `${qt.city} ` : ''}{qt.name}</h3>
                    <div className="team-card-record" style={{color: '#f59e0b'}}>Pending — awaiting league assignment</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button className="buy-team-btn" onClick={() => { setShowBuyModal(true); document.body.style.overflow = 'hidden'; }}>
          + Buy a New Team — {TEAM_COST.toLocaleString()} $BALLS
        </button>

        {/* Boost Inventory — visible even without a team */}
        {myBoosts.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ color: '#e8b82a', fontSize: '1rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚡ Boost Inventory ({myBoosts.length})
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
              {myBoosts.map((b: any) => {
                const def = b.boost_definitions;
                if (!def) return null;
                const color = '#9ca3af';
                const attrIcons: Record<string, string> = { power: '💪', contact: '🎯', speed: '⚡', fielding: '🧤', arm: '🦾', discipline: '🧠', endurance: '🫀', velocity: '🔥', control: '🎯', movement: '🌀', deception: '🎭', stamina: '🔋' };
                return (
                  <div key={b.id} style={{
                    background: 'rgba(16,24,40,0.6)',
                    border: `1px solid ${color}33`,
                    borderRadius: 8,
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <span style={{ fontSize: 20 }}>{attrIcons[def.target_attribute] || '⬆️'}</span>
                    <div>
                      <div style={{ color, fontSize: 13, fontWeight: 700 }}>{def.name}</div>
                      <div style={{ color: '#6b7280', fontSize: 11, textTransform: 'capitalize' }}>
                        {def.player_context === 'hitter' ? '⚾ Hitter' : def.player_context === 'pitcher' ? '🎯 Pitcher' : '🌐 Any'}
                        {b.is_equipped ? ' · ✅ Equipped' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showBuyModal && (
          <div className="buy-modal-overlay" onClick={() => { setShowBuyModal(false); document.body.style.overflow = ''; }}>
            <div className="buy-modal" onClick={e => e.stopPropagation()}>
              <div className="buy-modal-header">
                <h3>🏟️ Create Your Team</h3>
                <button className="buy-modal-close" onClick={() => { setShowBuyModal(false); document.body.style.overflow = ''; }}>×</button>
              </div>
              <div className="buy-modal-body">
                <p className="buy-modal-note">Cost: <strong>{TEAM_COST.toLocaleString()} $BALLS</strong></p>
                <p className="buy-modal-note" style={{fontSize:'0.8rem', color:'#9ca3af', marginTop:-8}}>Your team enters the queue and will be placed in a league at the next off-season.</p>
                <div className="buy-team-inputs">
                  <label>City:
                    <input type="text" value={newTeamCity} onChange={e => setNewTeamCity(e.target.value)} placeholder="e.g., Chicago" maxLength={20} className="buy-name-input" />
                  </label>
                  <label>Team Name:
                    <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} placeholder="e.g., Thunder" maxLength={30} className="buy-name-input" />
                  </label>
                  <label>Abbreviation:
                    <input type="text" value={newTeamAbbr} onChange={e => setNewTeamAbbr(e.target.value.toUpperCase().slice(0, 3))} placeholder="THR" maxLength={3} className="buy-abbr-input" />
                  </label>
                </div>
                <p className="buy-modal-note">Starts with an empty roster. Sign players from Free Agency during off-season!</p>
                <p className="buy-modal-note" style={{fontSize:'0.78rem', color:'#f59e0b', marginTop: 4}}>⚠️ Your team will be added to a league when the new season begins.</p>
                <div className="buy-confirm-btns">
                  <button className="buy-back-btn" onClick={() => { setShowBuyModal(false); document.body.style.overflow = ''; }}>Cancel</button>
                  <button className="buy-confirm-btn" disabled={!newTeamName || newTeamAbbr.length < 2 || buying} onClick={() => {
                    if (!newTeamName || newTeamAbbr.length < 2) { alert('Enter a team name and 2-3 letter abbreviation'); return; }
                    handleBuyTeam();
                  }}>
                    {buying ? 'Processing...' : `Buy Team — ${TEAM_COST.toLocaleString()} ⚾`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Team selected — show tabs
  return (
    <div className="my-teams-page">
      <button className="back-btn" onClick={() => { setSelectedTeam(null); setLineup([]); setPitchingRotation([]); }}>← Back to Teams</button>

      <div className="team-header-card">
        <div className="team-header-abbr" style={{ background: selectedTeam.primary_color || 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}>{selectedTeam.abbreviation}</div>
        <div className="team-header-info">
          <h1>{(selectedTeam.city && selectedTeam.name && !selectedTeam.name.toLowerCase().startsWith(selectedTeam.city.toLowerCase())) ? `${selectedTeam.city} ${selectedTeam.name}` : selectedTeam.name}</h1>
          <div className="team-header-meta">
            <span>{teamStanding ? `${teamStanding.wins}-${teamStanding.losses}` : `${selectedTeam.wins}-${selectedTeam.losses}`}</span>
            <span className="tier-badge" style={{ background: TIER_COLORS[selectedTeam.tier] }}>{TIER_NAMES[selectedTeam.tier]}</span>
          </div>
        </div>
      </div>

      <div className="team-tabs">
        {([
          ['roster', 'Roster'],
          ['lineup', 'Lineup'],
          ['playerStats', 'Player Stats'],
          ['teamStats', 'Team Stats'],
          ['schedule', 'Schedule'],
          ['boosts', 'Boosts'],
          ['offers', '📋 Offers'],
          ['strategy', 'Strategy'],
          ['uniforms', 'Uniforms'],
        ] as [TeamTab, string][]).map(([key, label]) => (
          <button key={key} className={`team-tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </div>

      <div className="team-tab-content">
        {activeTab === 'roster' && renderRoster()}
        {activeTab === 'lineup' && renderLineup()}
        {activeTab === 'playerStats' && renderPlayerStats()}
        {activeTab === 'teamStats' && renderTeamStats()}
        {activeTab === 'schedule' && renderSchedule()}
        {activeTab === 'boosts' && renderBoosts()}
        {activeTab === 'offers' && renderOffers()}
        {activeTab === 'strategy' && renderStrategy()}
        {activeTab === 'uniforms' && renderUniforms()}
      </div>

      {/* Boxscore Modal */}
      {boxscoreGameId && selectedTeam?.league_id && (
        <BoxScore gameId={boxscoreGameId} leagueId={selectedTeam.league_id} onClose={() => setBoxscoreGameId(null)} />
      )}
    </div>
  );
};

export default MyTeamsPage;
